/**
 * Cles SSH dans le coffre web (« SSH key » de Bitwarden, de 1Password et de Proton Pass ; type `ssh-key` deja
 * range par la CLI, R21).
 *
 * Forme des secrets : `privateKey` (bloc PEM / OpenSSH) et `publicKey` (ligne `algorithme base64 [commentaire]`),
 * a la racine des secrets, exactement comme `apps/cli/src/cli.ts` (`SshKeySecrets`) : une cle rangee par l'un est
 * lisible par l'autre, et l'agent SSH de la CLI signe avec une cle collee dans le web. Les deux vivent CHIFFREES
 * avec l'entree ; le serveur ne voit ni l'une ni l'autre.
 *
 * Module pur, sans crypto : il LIT des formats publies (RFC 4253 §6.6 pour la cle publique, PROTOCOL.key d'OpenSSH
 * pour l'en-tete `openssh-key-v1`, dont la cle publique est en clair meme quand la cle privee est chiffree) et
 * calcule l'empreinte `SHA256:` d'`ssh-keygen -l` avec SHA-256 de `@noble/hashes`. Il ne dechiffre ni ne genere rien.
 */
import { sha256 } from '@noble/hashes/sha2.js';
import { base64ToBytes, bytesToBase64, type EntrySecrets } from '@klavox/crypto-core';

/** Algorithmes de cle publique reconnus (OpenSSH 9). */
export const SSH_PUBLIC_KEY_ALGORITHMS: ReadonlySet<string> = new Set([
  'ssh-ed25519',
  'ssh-rsa',
  'ssh-dss',
  'ecdsa-sha2-nistp256',
  'ecdsa-sha2-nistp384',
  'ecdsa-sha2-nistp521',
  'sk-ssh-ed25519@openssh.com',
  'sk-ecdsa-sha2-nistp256@openssh.com',
]);

/** Libelle court d'un algorithme, pour la fiche. */
const ALGORITHM_LABELS: Record<string, string> = {
  'ssh-ed25519': 'Ed25519',
  'ssh-rsa': 'RSA',
  'ssh-dss': 'DSA',
  'ecdsa-sha2-nistp256': 'ECDSA P-256',
  'ecdsa-sha2-nistp384': 'ECDSA P-384',
  'ecdsa-sha2-nistp521': 'ECDSA P-521',
  'sk-ssh-ed25519@openssh.com': 'Ed25519 (cle materielle)',
  'sk-ecdsa-sha2-nistp256@openssh.com': 'ECDSA P-256 (cle materielle)',
};

/** Paire telle qu'elle vit dans les secrets de l'entree. */
export interface SshKeyMaterial {
  readonly privateKey: string;
  readonly publicKey: string;
}

/** Cle publique lue : algorithme, blob binaire, commentaire, empreinte `SHA256:` d'`ssh-keygen -l`. */
export interface SshPublicKeyInfo {
  readonly algorithm: string;
  readonly label: string;
  readonly blob: Uint8Array;
  readonly comment: string;
  readonly fingerprint: string;
}

/** Ce qui empeche d'enregistrer une paire saisie. */
export type SshKeyProblem = 'private-invalid' | 'public-invalid' | 'mismatch';

export const SSH_KEY_PROBLEM_MESSAGES: Record<SshKeyProblem, string> = {
  'private-invalid': 'Cle privee illisible : collez le bloc complet, de « -----BEGIN … PRIVATE KEY----- » a « -----END … PRIVATE KEY----- »',
  'public-invalid': 'Cle publique illisible : une ligne « ssh-ed25519 AAAA… commentaire » est attendue (fichier .pub)',
  mismatch: 'La cle publique ne correspond pas a la cle privee',
};

const PRIVATE_BLOCK = /^-----BEGIN ((?:[A-Z0-9]+ )*)PRIVATE KEY-----\n([\s\S]+?)\n-----END \1PRIVATE KEY-----$/u;
const OPENSSH_MAGIC = 'openssh-key-v1\0';

/** Lecteur de chaines SSH (`uint32` big-endian + octets, RFC 4251 §5). `null` des qu'une longueur deborde. */
export class SshReader {
  private offset = 0;
  constructor(private readonly bytes: Uint8Array) {}

  uint32(): number | null {
    if (this.offset + 4 > this.bytes.length) return null;
    const b = this.bytes;
    const value = ((b[this.offset]! << 24) >>> 0) + (b[this.offset + 1]! << 16) + (b[this.offset + 2]! << 8) + b[this.offset + 3]!;
    this.offset += 4;
    return value;
  }

  string(): Uint8Array | null {
    const length = this.uint32();
    if (length === null || this.offset + length > this.bytes.length) return null;
    const value = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return value;
  }

  done(): boolean {
    return this.offset === this.bytes.length;
  }

  remaining(): number {
    return this.bytes.length - this.offset;
  }
}

const latin1 = (bytes: Uint8Array): string => String.fromCharCode(...bytes);

function decodeBase64(text: string): Uint8Array | null {
  if (!/^[A-Za-z0-9+/]+={0,2}$/u.test(text)) return null;
  try {
    return base64ToBytes(text);
  } catch {
    return null;
  }
}

/** Empreinte d'un blob de cle publique, au format d'`ssh-keygen -l` (base64 sans remplissage). */
export function sshFingerprint(blob: Uint8Array): string {
  return `SHA256:${bytesToBase64(sha256(blob)).replace(/=+$/u, '')}`;
}

/** Lit une paire dans des secrets dechiffres : seules les chaines comptent. */
export function readSshKey(secrets: EntrySecrets): SshKeyMaterial {
  return {
    privateKey: typeof secrets['privateKey'] === 'string' ? secrets['privateKey'] : '',
    publicKey: typeof secrets['publicKey'] === 'string' ? secrets['publicKey'] : '',
  };
}

/** Bloc prive normalise : fins de ligne Unix, espaces de bord retires, une fin de ligne finale (ce qu'attend `ssh`). */
export function normalizePrivateKey(text: string): string {
  const trimmed = text.replace(/\r\n?/gu, '\n').trim();
  return trimmed.length === 0 ? '' : `${trimmed}\n`;
}

/** Ligne publique normalisee : une seule ligne, blancs reduits. */
export function normalizePublicKey(text: string): string {
  return text.trim().replace(/\s+/gu, ' ');
}

/** Un bloc `-----BEGIN … PRIVATE KEY-----` complet (OpenSSH, PKCS#1, PKCS#8, EC), au corps base64. */
export function isPrivateKeyBlock(text: string): boolean {
  const match = PRIVATE_BLOCK.exec(normalizePrivateKey(text).trimEnd());
  if (match === null) return false;
  // En-tetes PEM (`Proc-Type`, `DEK-Info`) toleres, puis du base64 uniquement.
  const body = match[2]!.split('\n').filter((line) => !line.includes(':')).join('');
  return body.length > 0 && decodeBase64(body) !== null;
}

/** Corps binaire d'une cle au format `openssh-key-v1`, ou `null`. */
function openSshBody(text: string): Uint8Array | null {
  const match = PRIVATE_BLOCK.exec(normalizePrivateKey(text).trimEnd());
  if (match === null || match[1] !== 'OPENSSH ') return null;
  const bytes = decodeBase64(match[2]!.split('\n').join(''));
  if (bytes === null || latin1(bytes.subarray(0, OPENSSH_MAGIC.length)) !== OPENSSH_MAGIC) return null;
  return bytes.subarray(OPENSSH_MAGIC.length);
}

/**
 * En-tete `openssh-key-v1` : chiffrement, puis le blob de la PREMIERE cle publique (en clair, meme quand la cle
 * privee est protegee par une phrase de passe).
 */
function readOpenSshHeader(text: string): { readonly cipher: string; readonly publicBlob: Uint8Array } | null {
  const body = openSshBody(text);
  if (body === null) return null;
  const reader = new SshReader(body);
  const cipher = reader.string();
  const kdf = reader.string();
  const kdfOptions = reader.string();
  const count = reader.uint32();
  const publicBlob = reader.string();
  if (cipher === null || kdf === null || kdfOptions === null || count === null || count < 1 || publicBlob === null) return null;
  return { cipher: latin1(cipher), publicBlob };
}

/** Paire Ed25519 lue en clair dans une cle `openssh-key-v1` NON chiffree. */
export interface OpenSshEd25519 {
  readonly seed: Uint8Array;
  readonly publicKey: Uint8Array;
  readonly comment: string;
}

/**
 * Graine, cle publique et commentaire d'une cle OpenSSH Ed25519 non chiffree (PROTOCOL.key) ; `null` pour tout
 * le reste (cle chiffree, autre algorithme, structure incoherente). Lecture seule : aucune arithmetique de courbe,
 * la cle publique doit etre celle de l'en-tete ET la moitie haute du secret, sinon la cle est refusee.
 */
export function readOpenSshEd25519(text: string): OpenSshEd25519 | null {
  const body = openSshBody(text);
  if (body === null) return null;
  const header = new SshReader(body);
  const cipher = header.string();
  const kdf = header.string();
  const kdfOptions = header.string();
  const count = header.uint32();
  const publicBlob = header.string();
  const section = header.string();
  if (cipher === null || kdf === null || kdfOptions === null || publicBlob === null || section === null || count !== 1) return null;
  if (latin1(cipher) !== 'none' || blobAlgorithm(publicBlob) !== 'ssh-ed25519') return null;
  const reader = new SshReader(section);
  const check1 = reader.uint32();
  const check2 = reader.uint32();
  const algorithm = reader.string();
  const publicKey = reader.string();
  const secret = reader.string();
  const comment = reader.string();
  if (check1 === null || check1 !== check2 || algorithm === null || latin1(algorithm) !== 'ssh-ed25519') return null;
  if (publicKey === null || publicKey.length !== 32 || secret === null || secret.length !== 64 || comment === null) return null;
  const headerKey = new SshReader(publicBlob);
  headerKey.string();
  const headerPublic = headerKey.string();
  const same = (a: Uint8Array, b: Uint8Array): boolean => a.length === b.length && a.every((byte, i) => byte === b[i]);
  if (headerPublic === null || !same(headerPublic, publicKey) || !same(secret.subarray(32), publicKey)) return null;
  return { seed: secret.slice(0, 32), publicKey: publicKey.slice(), comment: new TextDecoder().decode(comment) };
}

/** Nombre de champs prives qui suivent l'algorithme dans la section privee (`sshkey_private_serialize` d'OpenSSH). */
const OPENSSH_PRIVATE_FIELDS: Record<string, number> = {
  'ssh-ed25519': 2, // cle publique, secret (graine || cle publique)
  'ssh-rsa': 6, // n, e, d, iqmp, p, q
  'ecdsa-sha2-nistp256': 3, // courbe, point Q, scalaire d
  'ecdsa-sha2-nistp384': 3,
  'ecdsa-sha2-nistp521': 3,
};

/** Cle `openssh-key-v1` NON chiffree, lue champ par champ. */
export interface OpenSshPrivateKey {
  readonly algorithm: string;
  /** Blob de la cle publique de l'en-tete (en clair). */
  readonly publicBlob: Uint8Array;
  /** Champs prives bruts, dans l'ordre d'OpenSSH (`mpint` et `string` tels quels). */
  readonly fields: readonly Uint8Array[];
  readonly comment: string;
}

/**
 * Champs d'une cle OpenSSH non chiffree (Ed25519, RSA, ECDSA) ; `null` pour tout le reste (cle chiffree, DSA, cle
 * materielle `sk-`, plusieurs cles, structure incoherente : mots de controle differents, algorithme prive different
 * de celui de l'en-tete, octets de bourrage autres que 1, 2, 3…). Lecture seule.
 */
export function readOpenSshPrivateKey(text: string): OpenSshPrivateKey | null {
  const body = openSshBody(text);
  if (body === null) return null;
  const header = new SshReader(body);
  const cipher = header.string();
  const kdf = header.string();
  const kdfOptions = header.string();
  const count = header.uint32();
  const publicBlob = header.string();
  const section = header.string();
  if (cipher === null || kdf === null || kdfOptions === null || publicBlob === null || section === null || count !== 1) return null;
  const algorithm = blobAlgorithm(publicBlob);
  if (latin1(cipher) !== 'none' || algorithm === null || !header.done()) return null;
  const fieldCount = OPENSSH_PRIVATE_FIELDS[algorithm];
  const reader = new SshReader(section);
  const check1 = reader.uint32();
  const check2 = reader.uint32();
  const privateAlgorithm = reader.string();
  if (fieldCount === undefined || check1 === null || check1 !== check2 || privateAlgorithm === null || latin1(privateAlgorithm) !== algorithm) return null;
  const fields: Uint8Array[] = [];
  for (let i = 0; i < fieldCount; i += 1) {
    const field = reader.string();
    if (field === null) return null;
    fields.push(field.slice());
  }
  const comment = reader.string();
  if (comment === null) return null;
  const padding = section.subarray(section.length - reader.remaining());
  if (padding.some((byte, i) => byte !== i + 1)) return null;
  return { algorithm, publicBlob: publicBlob.slice(), fields, comment: new TextDecoder().decode(comment) };
}

/** Blob de cle publique valide : son algorithme est reconnu et repete en tete du blob. */
function blobAlgorithm(blob: Uint8Array): string | null {
  const name = new SshReader(blob).string();
  if (name === null) return null;
  const algorithm = latin1(name);
  return SSH_PUBLIC_KEY_ALGORITHMS.has(algorithm) ? algorithm : null;
}

/** Lit une ligne `algorithme base64 [commentaire]` (fichier `.pub`, `authorized_keys`) ; `null` si elle ne tient pas. */
export function parseSshPublicKey(line: string): SshPublicKeyInfo | null {
  const [algorithm, encoded, ...rest] = normalizePublicKey(line).split(' ');
  if (algorithm === undefined || encoded === undefined || !SSH_PUBLIC_KEY_ALGORITHMS.has(algorithm)) return null;
  const blob = decodeBase64(encoded);
  // L'algorithme annonce doit etre celui du blob : une ligne « ssh-ed25519 » portant une cle RSA est refusee.
  if (blob === null || blobAlgorithm(blob) !== algorithm) return null;
  return { algorithm, label: ALGORITHM_LABELS[algorithm] ?? algorithm, blob, comment: rest.join(' '), fingerprint: sshFingerprint(blob) };
}

/** Ligne publique tiree d'une cle privee OpenSSH (sans commentaire, il est dans la partie chiffree) ; `null` sinon. */
export function publicKeyOfPrivateKey(text: string): string | null {
  const header = readOpenSshHeader(text);
  if (header === null) return null;
  const algorithm = blobAlgorithm(header.publicBlob);
  return algorithm === null ? null : `${algorithm} ${bytesToBase64(header.publicBlob)}`;
}

/**
 * La cle privee est-elle protegee par une phrase de passe ? `true`/`false` pour OpenSSH et PEM chiffre/PKCS#8
 * chiffre, `null` si le format ne le dit pas.
 */
export function isPrivateKeyEncrypted(text: string): boolean | null {
  const header = readOpenSshHeader(text);
  if (header !== null) return header.cipher !== 'none';
  const normalized = normalizePrivateKey(text);
  if (!isPrivateKeyBlock(normalized)) return null;
  return normalized.startsWith('-----BEGIN ENCRYPTED PRIVATE KEY-----') || /^Proc-Type: 4,ENCRYPTED$/mu.test(normalized);
}

/** Paire normalisee, cle publique deduite de la cle privee OpenSSH quand elle manque. */
export function completeSshKey(material: SshKeyMaterial): SshKeyMaterial {
  const privateKey = normalizePrivateKey(material.privateKey);
  const publicKey = normalizePublicKey(material.publicKey);
  return { privateKey, publicKey: publicKey.length > 0 ? publicKey : (publicKeyOfPrivateKey(privateKey) ?? '') };
}

/**
 * Ce qui empeche d'enregistrer la paire, ou `null`. Une moitie vide est permise (une cle publique seule se range,
 * comme chez Bitwarden) ; une moitie illisible, non ; deux moities qui ne vont pas ensemble, jamais : la fiche
 * afficherait l'empreinte d'une cle et l'agent signerait avec une autre.
 */
export function checkSshKey(material: SshKeyMaterial): SshKeyProblem | null {
  const privateKey = normalizePrivateKey(material.privateKey);
  const publicKey = normalizePublicKey(material.publicKey);
  if (privateKey.length > 0 && !isPrivateKeyBlock(privateKey)) return 'private-invalid';
  if (publicKey.length === 0) return null;
  const parsed = parseSshPublicKey(publicKey);
  if (parsed === null) return 'public-invalid';
  const header = privateKey.length > 0 ? readOpenSshHeader(privateKey) : null;
  if (header !== null && sshFingerprint(header.publicBlob) !== parsed.fingerprint) return 'mismatch';
  return null;
}

/** Secrets avec la paire ecrite (moitie vide retiree) ; le reste de l'entree est garde tel quel. */
export function withSshKey(secrets: EntrySecrets, material: SshKeyMaterial): EntrySecrets {
  const { privateKey: _private, publicKey: _public, ...kept } = secrets;
  const complete = completeSshKey(material);
  return {
    ...kept,
    ...(complete.privateKey.length > 0 ? { privateKey: complete.privateKey } : {}),
    ...(complete.publicKey.length > 0 ? { publicKey: complete.publicKey } : {}),
  };
}
